import { useEffect, useRef } from 'react';

import { useEnrolmentSession } from '@/lib/enrolmentSession';
import { isCandidateSessionOpen, useKioskSession } from '@/lib/kioskSession';

/**
 * ⭐ **When a visit ends, the tablet asks once whether its venue still serves** [03-us-kad-6o AC-1; decision
 * 0433, derived from 0429 R2 — *the tablet lands on the pad at its next reset or reload*].
 *
 * The trigger is the candidate session CLOSING — `endCandidateSession`, by a finished visit, *Exit and
 * clear*, a Back out of the flow or the idle timeout, every one of which leaves `screen` on `welcome`.
 *
 * ⭐ **The candidate's data is already gone when the probe is sent** [AC-1]: the same `set` that closes the
 * session clears it, and this runs after that render has committed.
 *
 * ⛔ **A React effect, not a synchronous store subscription, and the difference is a real path.** A 401 on a
 * candidate-journey call runs `endCandidateSession()` and then `refuseCredential()` in one tick
 * [`onUnauthenticated`, 0245]. A subscriber would see the session close while the tablet was still enrolled
 * and send a probe the pad does not need; the effect runs after both, finds the tablet at the pad, and the
 * store's own guard sends nothing.
 *
 * ⛔ **Only a TRANSITION asks** — open to closed. A welcome that stays a welcome asks nothing: the
 * language-only idle reset [03-us-kad-1e AC-16] closes no visit, and the idle welcome re-checks on its first
 * tap instead [0430 § 1]. ⛔ No timer, no poller [0354].
 */
export function useVenueCheckOnReset(): void {
  const sessionOpen = useKioskSession(isCandidateSessionOpen);
  const checkVenueServes = useEnrolmentSession((state) => state.checkVenueServes);
  const wasOpen = useRef(sessionOpen);

  useEffect(() => {
    if (wasOpen.current && !sessionOpen) void checkVenueServes('reset');
    wasOpen.current = sessionOpen;
  }, [sessionOpen, checkVenueServes]);
}
